import React, { useState } from 'react'
import {
    confirmOrderSignInVerificationCode,
    prepareOrderAuth,
    resolveOrderVerificationErrorMessage,
    resolvePrepareOrderAuthErrorMessage,
    retrieveOrder,
    startOrderSignIn
} from '../../services/api/consumer'
import { ConsumerOrderDetails } from '../../model'
import { Controller, useForm } from 'react-hook-form'
import Button from '../../components/Button/Button'
import DynamicFaIcon from '../../components/DynamicFaIcon/DynamicFaIcon'
import OtpInput from '../../components/OtpInput/OtpInput'
import DefaultLoader from '../../components/Loaders/DefaultLoader'
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage'

type EmailFormData = {
    emailAddress: string
}

type VerificationFormData = {
    verificationCode: string
}

type VerifyStep = 'email' | 'code'

interface ConfirmOrderVerifyProps {
    token?: string
    onVerified: (res: ConsumerOrderDetails) => void
}

const INVALID_LINK_ERROR_MESSAGE = 'Deze bevestigingslink is ongeldig. Controleer de link uit de e-mail.'
const MISSING_EMAIL_ERROR_MESSAGE = 'Vul eerst het e-mailadres in dat bij de bestelling hoort.'

const ConfirmOrderVerify: React.FunctionComponent<ConfirmOrderVerifyProps> = ({
    token,
    onVerified
}: ConfirmOrderVerifyProps) => {
    const {
        register,
        formState: emailFormState,
        handleSubmit: handleEmailSubmit,
        reset: resetEmailForm
    } = useForm<EmailFormData>({
        defaultValues: {
            emailAddress: ''
        },
        mode: 'onChange',
        reValidateMode: 'onChange'
    })
    const {
        control,
        formState: verificationFormState,
        handleSubmit: handleVerificationSubmit,
        reset: resetVerificationForm
    } = useForm<VerificationFormData>({
        defaultValues: {
            verificationCode: ''
        },
        mode: 'onChange',
        reValidateMode: 'onChange'
    })
    const [step, setStep] = useState<VerifyStep>('email')
    const [isSubmittingEmailStep, setIsSubmittingEmailStep] = useState(false)
    const [isSubmittingCodeStep, setIsSubmittingCodeStep] = useState(false)
    const [verificationEmail, setVerificationEmail] = useState('')
    const [error, setError] = useState<string | null>(null)

    const hasOrderToken = Boolean(token)
    const isEmailStep = step === 'email'
    const isCodeStep = step === 'code'
    const displayedError = error ?? (!hasOrderToken ? INVALID_LINK_ERROR_MESSAGE : null)

    const sendVerificationCode = async (rawEmailAddress: string) => {
        const emailAddress = rawEmailAddress.trim()

        if (!emailAddress) {
            return false
        }

        if (!token) {
            setError(INVALID_LINK_ERROR_MESSAGE)
            return false
        }

        setError(null)
        setIsSubmittingEmailStep(true)

        try {
            try {
                // Step 1: prepare and validate order authentication with email and ordertoken
                await prepareOrderAuth({
                    email: emailAddress,
                    orderToken: token,
                    domain: window.location.hostname
                })
            } catch (prepareError) {
                setError(resolvePrepareOrderAuthErrorMessage(prepareError))
                return false
            }

            try {
                // Step 2: start order sign-in
                await startOrderSignIn(emailAddress)
            } catch (signInError) {
                console.error('Error starting order sign-in:', signInError)
                setError('Er is een fout opgetreden bij het starten van de verificatie. Probeer het opnieuw.')
                return false
            }

            setVerificationEmail(emailAddress)
            setStep('code')
            resetVerificationForm({ verificationCode: '' })
            return true
        } finally {
            setIsSubmittingEmailStep(false)
        }
    }

    const onSendVerificationCode = async (data: EmailFormData) => {
        await sendVerificationCode(data.emailAddress)
    }

    const onVerifyOrderAccess = async (data: VerificationFormData) => {
        if (!verificationEmail) {
            setStep('email')
            resetEmailForm({ emailAddress: '' })
            setError(MISSING_EMAIL_ERROR_MESSAGE)
            return
        }

        setError(null)
        setIsSubmittingCodeStep(true)

        try {
            try {
                // Step 3: confirm order sign-in verification code
                const verificationResult = await confirmOrderSignInVerificationCode(data.verificationCode.trim())

                if (!verificationResult.isSignedIn) {
                    throw new Error('Verification code is not accepted.')
                }
            } catch (verificationError) {
                setError(resolveOrderVerificationErrorMessage(verificationError))
                return
            }

            try {
                // Step 4: retrieve order
                const order = await retrieveOrder()
                onVerified(order)
            } catch (retrieveError) {
                console.error('Error retrieving order after verification:', retrieveError)
                setError('Er is een fout opgetreden bij het ophalen van de bestelling. Probeer het opnieuw.')
            }
        } finally {
            setIsSubmittingCodeStep(false)
        }
    }

    const onResendCode = async () => {
        if (!verificationEmail) {
            setStep('email')
            setError(MISSING_EMAIL_ERROR_MESSAGE)
            return
        }

        await sendVerificationCode(verificationEmail)
    }

    return (
        <>
            <div className='flex flex-col space-y-2'>
                <h3 className='text-2xl text-center'>Order bevestigen</h3>
                {isEmailStep && !isSubmittingEmailStep && hasOrderToken && (
                    <p className='text-sm text-[--tb-textLight] text-center'>
                        Vul het e-mailadres in waarop u het bevestigingsverzoek heeft ontvangen.
                    </p>
                )}
                {isSubmittingEmailStep && (
                    <div className='w-full flex items-center justify-center py-8 min-h-[175px]'>
                        <DefaultLoader loadingMessage='Verificatiecode wordt verzonden...' />
                    </div>
                )}
                {isCodeStep && !isSubmittingEmailStep && (
                    <p className='text-sm text-[--tb-textLight] text-center'>
                        De verificatiecode is verzonden naar <span className='font-medium'>{verificationEmail}</span>.
                    </p>
                )}
            </div>

            {isEmailStep && !isSubmittingEmailStep && (
                <form onSubmit={handleEmailSubmit(onSendVerificationCode)} className='flex flex-col space-y-4'>
                    <div className='flex flex-col space-y-1 text-center'>
                        <label
                            htmlFor='emailAddress'
                            className='text-sm font-medium text-[--tb-mainHeading] mb-1 block text-center'
                        >
                            E-mailadres
                        </label>
                        <input
                            {...register('emailAddress', {
                                required: 'E-mailadres is verplicht',
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: 'Ongeldig e-mailadres'
                                }
                            })}
                            id='emailAddress'
                            type='email'
                            autoComplete='email'
                            placeholder='Vul je e-mailadres in'
                            disabled={isSubmittingEmailStep || isSubmittingCodeStep}
                            className='w-full'
                        />
                        <ErrorMessage
                            message={
                                typeof emailFormState.errors.emailAddress?.message === 'string'
                                    ? emailFormState.errors.emailAddress.message
                                    : undefined
                            }
                        />
                    </div>

                    <div className='flex flex-col items-center justify-between gap-3 mb-4 w-full'>
                        {displayedError && <p className='text-red-700 text-sm text-center'>{displayedError}</p>}
                        <Button
                            type='submit'
                            disabled={!emailFormState.isValid || isSubmittingCodeStep || !hasOrderToken}
                        >
                            <span>Verificatiecode sturen</span>
                        </Button>
                    </div>
                </form>
            )}

            {isCodeStep && !isSubmittingEmailStep && (
                <form
                    onSubmit={handleVerificationSubmit(onVerifyOrderAccess)}
                    className='flex flex-col space-y-4 !mt-2'
                >
                    <Controller
                        name='verificationCode'
                        control={control}
                        rules={{
                            required: 'Voer de verificatiecode in',
                            pattern: {
                                value: /^\d{6}$/,
                                message: 'Voer een geldige 6-cijferige code in'
                            }
                        }}
                        render={({ field }) => (
                            <OtpInput
                                id='verificationCode'
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                disabled={isSubmittingEmailStep || isSubmittingCodeStep}
                                onComplete={handleVerificationSubmit(onVerifyOrderAccess)}
                            />
                        )}
                    />
                    <div className='flex flex-col items-center justify-between gap-3 mb-4 w-full'>
                        <ErrorMessage
                            message={
                                typeof verificationFormState.errors.verificationCode?.message === 'string'
                                    ? verificationFormState.errors.verificationCode.message
                                    : undefined
                            }
                        />
                        {displayedError && <p className='text-red-700 text-sm text-center'>{displayedError}</p>}
                        <Button
                            type='submit'
                            disabled={!verificationFormState.isValid || isSubmittingCodeStep || isSubmittingEmailStep}
                        >
                            {isSubmittingCodeStep ? (
                                <>
                                    <DynamicFaIcon iconName='spinner' className='mr-2' spin />
                                    Code controleren..
                                </>
                            ) : (
                                <span>Volgende stap</span>
                            )}
                        </Button>
                        <div className='text-sm text-center'>
                            Geen code ontvangen?{' '}
                            <button
                                type='button'
                                onClick={() => void onResendCode()}
                                disabled={isSubmittingEmailStep || isSubmittingCodeStep}
                                className='text-[--tb-linkColor] underline disabled:no-underline disabled:opacity-50'
                            >
                                {isSubmittingEmailStep ? 'Code verzenden..' : 'Nieuwe code sturen'}
                            </button>
                        </div>
                    </div>
                </form>
            )}
        </>
    )
}
export default ConfirmOrderVerify
